/**
 * AddToCart Webhook for International Markets
 * 
 * Sends AddToCart event to n8n when products are added to cart
 */

(function() {
  'use strict';
  
  // Check if helper is available
  if (!window.WebhookHelper || !window.WebhookHelper.isInternationalMarket()) {
    return;
  }
  
  // Track previous cart state to identify NEW items only
  let previousCartItems = new Set();
  let previousCartItemCount = 0;
  let processing = false; // Prevent concurrent processing
  
  // Initialize cart state on page load
  function initializeCartState() {
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        if (cart && cart.items) {
          previousCartItemCount = cart.items.length;
          previousCartItems.clear();
          cart.items.forEach(item => {
            if (item.product_id && item.variant_id) {
              const key = `${item.product_id}-${item.variant_id}`;
              previousCartItems.add(key);
            }
          });
        }
      })
      .catch(() => {
        // Silently ignore
      });
  }
  
  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCartState);
  } else {
    initializeCartState();
  }
  
  // Extract item data from add-to-cart event
  function extractItemData(event) {
    const response = event.detail?.response;
    
    if (!response) {
      return null;
    }
    
    // Method 1: response.item (single item - most reliable)
    if (response.item && response.item.product_id && response.item.variant_id) {
      return response.item;
    }
    
    // Method 2: response.items array - get the last item (newly added)
    if (response.items && Array.isArray(response.items) && response.items.length > 0) {
      const lastItem = response.items[response.items.length - 1];
      if (lastItem && lastItem.product_id && lastItem.variant_id) {
        return lastItem;
      }
    }
    
    // Method 3: response itself is the item
    if (response.product_id && response.variant_id) {
      return response;
    }
    
    return null;
  }
  
  // Check if item is NEW (not in previous cart)
  function isNewItem(item) {
    if (!item || !item.product_id || !item.variant_id) {
      return false;
    }
    const key = `${item.product_id}-${item.variant_id}`;
    return !previousCartItems.has(key);
  }
  
  // Update cart state after processing
  function updateCartState(item) {
    if (item && item.product_id && item.variant_id) {
      const key = `${item.product_id}-${item.variant_id}`;
      previousCartItems.add(key);
      previousCartItemCount++;
    }
  }
  
  // Send AddToCart webhook
  function sendAddToCartWebhook(item) {
    if (!item || !item.product_id || !item.variant_id) {
      return;
    }
    
    // Only process NEW items
    if (!isNewItem(item)) {
      return;
    }
    
    const quantity = item.quantity || 1;
    
    // Get price (in INR cents)
    const priceInCents = item.final_price || item.price || item.line_price || 0;
    if (!priceInCents || priceInCents <= 0) {
      return;
    }
    
    const marketConfig = window.WebhookHelper.getMarketConfig();
    
    // Convert price and round up
    const priceInMarketCurrency = window.WebhookHelper.roundUpPrice(
      window.WebhookHelper.convertToMarketCurrency(priceInCents)
    );
    const totalValue = priceInMarketCurrency * quantity;
    
    // Prefix content IDs
    const variantId = item.variant_id.toString();
    const prefixedIds = window.WebhookHelper.prefixContentIds([variantId]);
    const prefixedVariantId = prefixedIds[0] || variantId;
    
    // Get product name
    const productName = item.product_title || item.title || '';
    
    // Send webhook
    window.WebhookHelper.sendWebhook('AddToCart', {
      custom_data: {
        content_type: 'product',
        content_ids: [prefixedVariantId],
        content_name: productName,
        value: totalValue,
        currency: marketConfig.currency,
        num_items: quantity,
        market: marketConfig.market,
        gateway: 'stripe', // Default to stripe for international
        catalog_prefix: marketConfig.catalogPrefix,
        locale: marketConfig.locale,
        contents: [{
          id: prefixedVariantId,
          sku: prefixedVariantId,
          item_price: priceInMarketCurrency,
          quantity: quantity,
          currency: marketConfig.currency
        }]
      },
      product_data: {
        product_id: item.product_id.toString(),
        variant_id: item.variant_id.toString(),
        quantity: quantity,
        price_in_cents: priceInCents,
        price_converted: priceInMarketCurrency
      }
    });
    
    // Update cart state after sending webhook
    updateCartState(item);
  }
  
  // Handle add-to-cart event
  function handleAddToCart(event) {
    // Prevent concurrent processing
    if (processing) {
      return;
    }
    
    processing = true;
    
    const item = extractItemData(event);
    
    if (item && isNewItem(item)) {
      // We have the new item from the event
      setTimeout(() => {
        sendAddToCartWebhook(item);
        processing = false;
      }, 0);
    } else {
      // Fallback: Fetch cart and find NEW items
      setTimeout(() => {
        fetch('/cart.js')
          .then(res => res.json())
          .then(cart => {
            if (cart && cart.items && cart.items.length > previousCartItemCount) {
              // Find items that are NEW (not in previous cart)
              const newItems = cart.items.filter(cartItem => {
                if (!cartItem.product_id || !cartItem.variant_id) return false;
                const key = `${cartItem.product_id}-${cartItem.variant_id}`;
                return !previousCartItems.has(key);
              });
              
              // Only process the first new item found
              if (newItems.length > 0) {
                sendAddToCartWebhook(newItems[0]);
              }
            }
            processing = false;
          })
          .catch(() => {
            processing = false;
          });
      }, 100);
    }
  }
  
  // Track last processed event to prevent duplicates
  let lastProcessedEvent = null;
  let lastProcessedTime = 0;
  const EVENT_DEBOUNCE_MS = 500; // Prevent processing same event within 500ms
  
  // Create unique event identifier
  function getEventId(event) {
    const item = extractItemData(event);
    if (item && item.product_id && item.variant_id) {
      return `${event.type}-${item.product_id}-${item.variant_id}`;
    }
    return `${event.type}-${event.timeStamp}`;
  }
  
  // Listen for Shopify's add-to-cart event (primary)
  document.addEventListener('theme:product:add', function(event) {
    const eventId = getEventId(event);
    const now = Date.now();
    
    // Skip if we just processed this exact event
    if (lastProcessedEvent === eventId && (now - lastProcessedTime) < EVENT_DEBOUNCE_MS) {
      return;
    }
    
    lastProcessedEvent = eventId;
    lastProcessedTime = now;
    
    handleAddToCart(event);
  }, false);
  
  // Fallback: Listen for cart:add event (only if theme:product:add didn't fire)
  document.addEventListener('theme:cart:add', function(event) {
    const eventId = getEventId(event);
    const now = Date.now();
    
    // Skip if we just processed this exact event
    if (lastProcessedEvent === eventId && (now - lastProcessedTime) < EVENT_DEBOUNCE_MS) {
      return;
    }
    
    // Add delay to give theme:product:add priority
    setTimeout(() => {
      // Check again after delay
      if (lastProcessedEvent === eventId && (Date.now() - lastProcessedTime) < EVENT_DEBOUNCE_MS) {
        return; // Already processed
      }
      
      lastProcessedEvent = eventId;
      lastProcessedTime = Date.now();
      
      handleAddToCart(event);
    }, 150);
  }, false);
  
})();
